<template>
  <v-container
    v-if="$can('view', 'Yacht') && !yacht.loading"
    fluid
    class="pa-0"
  >
    <!--<editor-fold description="Page Header">-->
    <yacht-details-header
      :yacht="yacht.data"
      :document-templates="documentTemplates.data"
      @edit:pricing="openPricingDialog"
      @edit:yacht="yachtEditDialog = true"
      @document:select-type="documentPreview.selectTypeModal = true"
      @comments:open="commentsDialog = true"
    />
    <!--</editor-fold>-->

    <v-divider />

    <!--    <v-row no-gutters>-->
    <!--      <v-col>-->
    <!--        <navigation :yacht="yacht.data" />-->
    <!--      </v-col>-->
    <!--      <v-spacer />-->
    <!--      <v-col>-->
    <!--        <status-section-->
    <!--          :yacht="yacht.data"-->
    <!--          @update:status="initiateUpdateStatus"-->
    <!--          @update:investment-status="initiateUpdateInvestmentStatus"-->
    <!--          @create:offer="pricing.dialog = true"-->
    <!--          @approval="openReviewDealDialog"-->
    <!--          @edit:delivery-plan="openDeliveryPlanDialog"-->
    <!--          @revert:investment-status="openRevertInvestmentStatusDialog"-->
    <!--          @revert:logistics-status="openRevertLogisticsStatusDialog"-->
    <!--        />-->
    <!--      </v-col>-->
    <!--    </v-row>-->

    <v-row
      class="mt-6"
      no-gutters
      justify="center"
    >
      <v-col cols="14">
        <v-fade-transition mode="out-in">
          <router-view
            :yacht.sync="yacht.data"
            @edit:delivery-plan="openDeliveryPlanDialog"
            @edit:yacht="yachtEditDialog = true"
            @edit:yacht-basic-details="yachtEditBasicDetailsDialog = true"
            @edit:pricing="pricing.dialog = true"
            @edit:deal-specifics="dealSpecificsEditDialog = true"
            @edit:serial-numbers="yachtEngineSerialsDialog = true"
            @update:status="initiateUpdateStatus"
            @document:preview="openDocumentPreview"
            @update:investment-status="initiateUpdateInvestmentStatus"
            @create:offer="startCreateOfferWizard()"
            @approval="openReviewDealDialog"
            @revert:investment-status="openRevertInvestmentStatusDialog"
            @revert:logistics-status="openRevertLogisticsStatusDialog"
            @update:yacht="fetchYacht()"
            @update:sales-rep="salesRepEditDialog = true"
          />
        </v-fade-transition>
      </v-col>
    </v-row>

    <new-document-dialog
      v-if="yacht.data"
      :key="documentTemplates.currentId || 'offer'"
      :yacht-id="yacht.data.id"
      :content="documentPreview.html"
      :template-id="documentTemplates.currentId"
      :document-type="documentPreview.type"
      :read-only="documentPreview.readOnly"
      :force-show-equipment-price="documentPreview.forceShowEquipmentPrice"
      :visible.sync="documentPreview.dialog"
      @document:created="documentCreated"
    />

    <pricing-dialog
      v-if="$can('set-finance-model', 'Yacht') && !!yacht.data"
      :yacht.sync="yacht.data"
      :visible.sync="pricing.dialog"
      @update:yacht="onYachtUpdated"
    />

    <yacht-edit-dialog
      v-if="$can('edit', 'Yacht') && yacht.data && !yacht.loading"
      :visible.sync="yachtEditDialog"
      :yacht="yacht.data"
      @update:yacht="fetchYacht"
    />

    <yacht-basic-details-edit-dialog
      v-if="$can('edit-basic-details', 'Yacht') && yacht.data && !yacht.loading"
      :visible.sync="yachtEditBasicDetailsDialog"
      :yacht="yacht.data"
      @update:yacht="fetchYacht"
    />

    <sales-status-dialog
      :yacht.sync="yacht.data"
      :visible.sync="updateStatus.salesDialog"
    />

    <approval-status-dialog
      :yacht.sync="yacht.data"
      :visible.sync="updateStatus.approvalDialog"
    />

    <reserved-status-dialog
      :yacht.sync="yacht.data"
      :visible.sync="updateStatus.reservedDialog"
    />

    <reject-deal-dialog
      :yacht.sync="yacht.data"
      :visible.sync="reviewDeal.reject.dialog"
    />

    <approve-deal-dialog
      :yacht.sync="yacht.data"
      :visible.sync="reviewDeal.approve.dialog"
    />

    <sold-status-dialog
      :yacht.sync="yacht.data"
      :visible.sync="updateStatus.soldDialog"
      @edit:delivery-plan="openDeliveryPlanDialog"
    />

    <revert-investment-status-dialog
      v-if="$can('revert-investment-status', 'Yacht')"
      :yacht.sync="yacht.data"
      :visible.sync="updateStatus.revertInvestmentStatusDialog"
    />

    <revert-logistics-status-dialog
      v-if="$can('revert-logistics-status', 'Yacht')"
      :yacht.sync="yacht.data"
      :visible.sync="updateStatus.revertLogisticsStatusDialog"
    />

    <delivery-plan-dialog
      :yacht.sync="yacht.data"
      :step="deliveryPlan.step"
      :visible.sync="deliveryPlan.dialog"
    />

    <pending-plan-status-dialog
      v-if="yacht.data.status === yacht.statuses.STATUS_DRAFT"
      :yacht.sync="yacht.data"
      :visible.sync="updateStatus.pendingPlanDialog"
    />

    <document-type-selector-dialog
      :visible.sync="documentPreview.selectTypeModal"
      :document-templates="documentTemplates.data"
      @document:preview="openDocumentPreview"
    />

    <deal-specifics-edit-dialog
      :visible.sync="dealSpecificsEditDialog"
      :yacht.sync="yacht.data"
    />

    <yacht-engine-serial-numbers-edit-dialog
      :yacht="yacht.data"
      :visible.sync="yachtEngineSerialsDialog"
    />

    <comments-dialog
      v-if="yacht.data"
      :visible.sync="commentsDialog"
      :commentable-type="commentableType"
      :commentable-id="yacht.data.id"
    />

    <yacht-sales-rep-edit-dialog
      v-if="yacht.data"
      :visible.sync="salesRepEditDialog"
      :yacht.sync="yacht.data"
    />
  </v-container>
</template>

<script>
import Document from '@/models/Document'
import File from '@/models/File'
import DocumentTemplate from '@/models/DocumentTemplate'
import Yacht from '@/models/Yacht'
import NewDocumentDialog from '@/components/business/yachts/NewDocumentDialog'
import SalesStatusDialog from '@/components/business/yachts/details/statusDialogs/SalesStatusDialog'
import PricingDialog from '@/components/business/yachts/PricingDialog'
import YachtDetailsHeader from '@/components/business/yachts/details/YachtDetailsHeader'
import ApprovalStatusDialog from '@/components/business/yachts/details/statusDialogs/ApprovalStatusDialog'
import YachtEditDialog from '@/components/business/yachts/YachtEditDialog'
import RejectDealDialog from '@/components/business/yachts/details/statusDialogs/RejectDealDialog'
import RevertInvestmentStatusDialog
  from '@/components/business/yachts/details/statusDialogs/RevertInvestmentStatusDialog'
import RevertLogisticsStatusDialog from '@/components/business/yachts/details/statusDialogs/RevertLogisticsStatusDialog'
import ApproveDealDialog from '@/components/business/yachts/details/statusDialogs/ApproveDealDialog'
import ReservedStatusDialog from '@/components/business/yachts/details/statusDialogs/ReservedStatusDialog'
import SoldStatusDialog from '@/components/business/yachts/details/statusDialogs/SoldStatusDialog'
import DeliveryPlanDialog from '@/components/business/yachts/DeliveryPlanDialog'
import DocumentTypeSelectorDialog from '@/components/business/yachts/DocumentTypeSelectorDialog'
import PendingPlanStatusDialog from '@/components/business/yachts/details/statusDialogs/PendingPlanStatusDialog'
import DealSpecificsEditDialog from '@/components/business/yachts/AgreementDetailsEditDialog'
import YachtEngineSerialNumbersEditDialog from '@/components/business/yachts/YachtEngineSerialNumbersEditDialog'
import CommentsDialog from '@/components/helpers/CommentsListDialog'
import YachtBasicDetailsEditDialog from '@/components/business/yachts/YachtBasicDetailsEditDialog'
import { sortBy } from 'lodash'
import YachtSalesRepEditDialog from '@/components/business/yachts/YachtSalesRepEditDialog'

export default {
  name: 'Yacht',
  components: {
    YachtSalesRepEditDialog,
    YachtBasicDetailsEditDialog,
    YachtEngineSerialNumbersEditDialog,
    DealSpecificsEditDialog,
    PendingPlanStatusDialog,
    DocumentTypeSelectorDialog,
    DeliveryPlanDialog,
    SoldStatusDialog,
    ApproveDealDialog,
    RejectDealDialog,
    ApprovalStatusDialog,
    RevertLogisticsStatusDialog,
    RevertInvestmentStatusDialog,
    YachtDetailsHeader,
    PricingDialog,
    NewDocumentDialog,
    SalesStatusDialog,
    YachtEditDialog,
    CommentsDialog,
    ReservedStatusDialog
  },
  data() {
    return {
      yacht: {
        data: null,
        statuses: Yacht,
        loading: false
      },
      documentTemplates: {
        data: [],
        currentId: null,
        loading: false
      },
      documentPreview: {
        html: null,
        dialog: false,
        loadingPreview: false,
        submitting: false,
        type: DocumentTemplate.TYPE_CONTRACT,
        readOnly: false,
        selectTypeModal: false,
        forceShowEquipmentPrice: false
      },
      updateStatus: {
        salesDialog: false,
        approvalDialog: false,
        reservedDialog: false,
        soldDialog: false,
        deliveryDialog: false,
        pendingPlanDialog: false,
        revertLogisticsStatusDialog: false,
        revertInvestmentStatusDialog: false
      },
      pricing: {
        dialog: false
      },
      reviewDeal: {
        approve: {
          dialog: false
        },
        reject: {
          dialog: false,
          reason: null
        }
      },
      deliveryPlan: {
        dialog: false,
        step: null
      },
      dealSpecificsEditDialog: false,
      yachtEditDialog: false,
      yachtEditBasicDetailsDialog: false,
      yachtEngineSerialsDialog: false,
      commentsDialog: false,
      salesRepEditDialog: false,
      createOfferWizardActive: false
    }
  },
  computed: {
    commentableType() {
      return Yacht.CLASS
    }
  },
  watch: {
    'pricing.dialog': function (newValue) {
      if (!newValue && this.createOfferWizardActive === true) this.yachtEditBasicDetailsDialog = true
    },
    'yachtEditBasicDetailsDialog': function (newValue) {
      if (!newValue && this.createOfferWizardActive === true) this.dealSpecificsEditDialog = true
    },
    'dealSpecificsEditDialog': function (newValue) {
      if (!newValue && this.createOfferWizardActive === true) {
        this.createOfferWizardActive = false
        this.documentPreview.selectTypeModal = true
      }
    }
  },
  created() {
    this.fetchYacht()
  },
  methods: {
    async fetchYacht() {
      if (!this.yacht.data) this.yacht.loading = true
      try {
        this.yacht.data = await Yacht.api.fetchYacht(this.$route.params.id)

        this.yacht.data.equipment_packs = sortBy(this.yacht.data.equipment_packs, 'category.order')

        this.$nextTick(() => {
          this.fetchDocumentTemplates()
        })
      } catch (e) {
        this.$store.dispatch('alert', {
          'text': 'Something went wrong',
          'color': 'error'
        })
      } finally {
        this.yacht.loading = false
      }
    },
    async fetchDocumentTemplates() {
      if (!this.$can('view', 'DocumentTemplate')) return

      this.documentTemplates.loading = true
      try {
        this.documentTemplates.data = await DocumentTemplate.api.fetch({
          country_id: this.yacht.data.country_id || undefined,
          finance_model_id: this.yacht.data.finance_model ? this.yacht.data.finance_model.id : undefined
        })
      } catch (e) {
        this.$store.dispatch('alert', {
          'text': 'Something went wrong',
          'color': 'error'
        })
      } finally {
        this.documentTemplates.loading = false
      }
    },
    async openDocumentPreview(type, documentTemplateId = null, equipmentPackId = null, annexShowEquipmentPrice = false) {
      this.documentPreview.loadingPreview = true

      let data = {
        yacht_id: this.yacht.data.id,
        type: type,
        document_template_id: documentTemplateId || undefined,
        yacht_equipment_pack_id: equipmentPackId || undefined,
        show_equipment_price: annexShowEquipmentPrice
      }

      try {
        let html = await Document.api.createPreview(data)

        const documentTemplate = this.documentTemplates.data.find(t => t.id === documentTemplateId)

        let readOnly = true

        if (documentTemplate) readOnly = !documentTemplate.editable
        if (type === 'order') readOnly = false

        this.documentTemplates.currentId = documentTemplateId
        this.documentPreview.html = html
        this.documentPreview.type = type
        this.documentPreview.readOnly = readOnly
        this.documentPreview.forceShowEquipmentPrice = annexShowEquipmentPrice
        this.documentPreview.dialog = true
      } catch (e) {
        this.$store.dispatch('alert', {
          'text': 'Something went wrong',
          'color': 'error'
        })

        throw e
      } finally {
        this.documentPreview.loadingPreview = false
      }
    },
    initiateUpdateStatus(newStatus) {
      if (newStatus === Yacht.STATUS_PENDING) this.updateStatus.pendingPlanDialog = true

      if (newStatus === Yacht.STATUS_PHASED_OUT) this.phaseOut()
    },
    initiateUpdateInvestmentStatus(newStatus) {
      if (newStatus === Yacht.INVESTMENT_STATUS_SALES) this.updateStatus.salesDialog = true
      if (newStatus === Yacht.INVESTMENT_STATUS_APPROVAL) this.updateStatus.approvalDialog = true
      if (newStatus === Yacht.INVESTMENT_STATUS_RESERVED) this.updateStatus.reservedDialog = true
      if (newStatus === Yacht.INVESTMENT_STATUS_SOLD) this.updateStatus.soldDialog = true
    },
    openReviewDealDialog(decision) {
      if (decision === 'approve') this.reviewDeal.approve.dialog = true
      if (decision === 'reject') this.reviewDeal.reject.dialog = true
    },
    openDeliveryPlanDialog(stepId = null) {
      this.deliveryPlan.step = stepId
      this.deliveryPlan.dialog = true

      this.$nextTick(() => {
        this.deliveryPlan.step = null
      })
    },
    async documentCreated(document) {
      this.documentPreview.selectTypeModal = false
      this.yacht.data.documents.unshift(document)
      await File.api.openDocument(document.file.id)
    },
    openPricingDialog(dialogVisible = true) {
      if (!this.$can('set-finance-model', 'Yacht')) return

      this.pricing.dialog = dialogVisible
    },
    openRevertInvestmentStatusDialog: function () {
      this.updateStatus.revertInvestmentStatusDialog = true
    },
    openRevertLogisticsStatusDialog: function () {
      this.updateStatus.revertLogisticsStatusDialog = true
    },
    onYachtUpdated(yacht) {
      this.yacht.data = yacht

      this.$nextTick(() => {
        this.fetchDocumentTemplates()
      })
    },
    async phaseOut() {
      try {
        await Yacht.api.updateYacht({
          status: Yacht.STATUS_PHASED_OUT
        }, this.yacht.data.id)

        this.yacht.data.status = Yacht.STATUS_PHASED_OUT

        this.$store.dispatch('alert', {
          text: 'Yacht is phased out!',
          color: 'success'
        })
      } catch (e) {
        this.$emit('alert', {
          text: 'Something went wrong',
          color: 'error'
        })
      }
    },
    startCreateOfferWizard() {
      this.createOfferWizardActive = true

      this.openPricingDialog(true)
    }
  }
}
</script>
